import {
  type FarmlandAreaRangeOption,
  type FarmlandAreaUnit,
  type FarmlandCategoricalFilterConfig,
  type FarmlandEndStagesDateRangeOption,
  type FarmlandFilterOption,
  type FarmlandFilterValues,
} from "@/features/map/types/farmland";

export const DEFAULT_FARMLAND_FILTER: FarmlandFilterValues = {
  classificationOfLandCodes: [],
  areaUnit: "squareMeter",
  areaFrom: null,
  areaTo: null,
  ownerFarmIntentionCodes: [],
  usageSituationInvestigationResultCodes: [],
  ownerStatementIntentSurveyResultsCodes: [],
  sectionOfNoushinhouCodes: [],
  sectionOfToshikeikakuhouCodes: [],
  kindOfRightCodes: [],
  endStagesDateFrom: null,
  endStagesDateTo: null,
  rightSettingContentsCodes: [],
};

/**
 * 農地タイルのコード/名称の表記揺れを、同じUI選択で一致させるための選択肢を生成する。
 *
 * タイル値は原則コードだが、生成元差分に備えてlabelと追加aliasも一致対象に含める。
 */
const option = (
  id: string,
  label: string,
  values: string[] = []
): FarmlandFilterOption => ({
  id,
  label,
  values: [id, label, ...values],
});

export const FARMLAND_AREA_RANGE_OPTIONS: Record<
  FarmlandAreaUnit,
  FarmlandAreaRangeOption[]
> = {
  squareMeter: [
    { id: "sqm-0-1000", label: "0~1,000", from: 0, to: 1000 },
    { id: "sqm-1000-2000", label: "1,000~2,000", from: 1000, to: 2000 },
    { id: "sqm-2000-3000", label: "2,000~3,000", from: 2000, to: 3000 },
    { id: "sqm-3000-5000", label: "3,000~5,000", from: 3000, to: 5000 },
    { id: "sqm-5000-10000", label: "5,000~10,000", from: 5000, to: 10000 },
    { id: "sqm-10000-50000", label: "10,000~50,000", from: 10000, to: 50000 },
    { id: "sqm-50000-over", label: "50,000~", from: 50000, to: null },
  ],
  are: [
    { id: "are-0-10", label: "0~10", from: 0, to: 10 },
    { id: "are-10-20", label: "10~20", from: 10, to: 20 },
    { id: "are-20-30", label: "20~30", from: 20, to: 30 },
    { id: "are-30-50", label: "30~50", from: 30, to: 50 },
    { id: "are-50-100", label: "50~100", from: 50, to: 100 },
    { id: "are-100-500", label: "100~500", from: 100, to: 500 },
    { id: "are-500-over", label: "500~", from: 500, to: null },
  ],
};

export const FARMLAND_END_STAGES_DATE_RANGE_OPTIONS: FarmlandEndStagesDateRangeOption[] =
  [
    { id: "withinThreeMonths", label: "三か月以内" },
    { id: "withinSixMonths", label: "半年以内" },
    { id: "withinOneYear", label: "１年以内" },
    { id: "withinTwoYears", label: "２年以内" },
    { id: "withinThreeYears", label: "３年以内" },
    { id: "overThreeYears", label: "３年以上" },
  ];

const CLASSIFICATION_OF_LAND_OPTIONS: FarmlandFilterOption[] = [
  option("1", "田"),
  option("2", "畑"),
  option("3", "樹園地"),
  option("4", "採草放牧地"),
  option("8", "農業用施設用地"),
  option("9", "その他"),
  option("999", "調査中"),
  option("0", "設定なし"),
];

const OWNER_FARM_INTENTION_OPTIONS: FarmlandFilterOption[] = [
  option("2", "貸したい"),
  option("4", "農地中間管理機構へ貸したい"),
  option("1", "売りたい"),
  // eMAFF本家は「その他」をコード6と3の両方で検索するため、3も一致対象に含める
  option("6", "その他", ["上記以外", "3"]),
  option("0", "農地に関する意向の表明なし"),
  option("5", "自ら耕作したい"),
  option("7", "調査中"),
  option("8", "経営を委託したい"),
  option("9", "農作業を委託したい"),
  option("10", "農地を交換したい"),
  option("999", "非公表"),
];

const USAGE_SITUATION_INVESTIGATION_RESULT_OPTIONS: FarmlandFilterOption[] = [
  option("6", "遊休農地（不耕作緑）"),
  option("7", "遊休農地（不耕作黄）"),
  option("1", "遊休農地（不耕作）※分類中"),
  option("2", "遊休農地（低利用）"),
  option("3", "遊休農地ではない"),
  option("5", "立入困難等外因的理由で調査不可"),
  option("4", "その他"),
  option("9", "調査中"),
];

const OWNER_STATEMENT_INTENT_SURVEY_RESULT_OPTIONS: FarmlandFilterOption[] = [
  option("1", "自ら耕作を再開"),
  option("2", "農地中間管理事業の利用"),
  option("3", "農地所有者代理事業の利用"),
  option("4", "自ら所有権の移転又は賃借権その他の権利の設定若しくは移転"),
  option("5", "その他（農業上の利用以外等）"),
  option("6", "調査対象外"),
  option("9", "調査中"),
];

const SECTION_OF_NOUSHINHOU_OPTIONS: FarmlandFilterOption[] = [
  option("1", "農業振興地域内・農用地区域内(青地)"),
  option("2", "農業振興地域内・農用地区域外(白地)"),
  option("3", "農業振興地域外"),
  option("5", "その他"),
  option("9", "調査中"),
  option("0", "設定なし"),
];

const SECTION_OF_TOSHIKEIKAKUHOU_OPTIONS: FarmlandFilterOption[] = [
  option("2", "市街化調整区域"),
  option("3", "非線引き都市計画の「用途地域」"),
  option("4", "都市計画区域外"),
  option("0", "設定なし"),
  option("5", "その他（用途地域等）"),
  option("9", "調査中"),
];

const KIND_OF_RIGHT_OPTIONS: FarmlandFilterOption[] = [
  option("0", "賃借権等の設定がない"),
  option("5", "賃借権"),
  option("7", "賃借権（期間借地）"),
  option("4", "使用貸借権"),
  option("6", "使用貸借権（期間借地）"),
  option("1", "地上権"),
  option("2", "永小作権"),
  option("3", "質権"),
  option("10", "その他の権利"),
  option("99", "調査中"),
];

const RIGHT_SETTING_CONTENTS_OPTIONS: FarmlandFilterOption[] = [
  option("0", "農地中間管理権が設定されていない"),
  option("1", "農地中間管理機構が既に貸出済の農地"),
  option("3", "農地中間管理機構が借り手・買い手を募集している農地"),
];

export const FARMLAND_CATEGORICAL_FILTER_CONFIGS: FarmlandCategoricalFilterConfig[] =
  [
    {
      title: "地目",
      filterKey: "classificationOfLandCodes",
      tilePropertyKey: "classification_of_land_code",
      options: CLASSIFICATION_OF_LAND_OPTIONS,
    },
    {
      title: "所有者の農地に関する意向",
      filterKey: "ownerFarmIntentionCodes",
      tilePropertyKey: "owner_farm_intention_code",
      options: OWNER_FARM_INTENTION_OPTIONS,
    },
    {
      title: "遊休農地かどうか",
      filterKey: "usageSituationInvestigationResultCodes",
      tilePropertyKey: "usage_situation_investigation_result_code",
      options: USAGE_SITUATION_INVESTIGATION_RESULT_OPTIONS,
    },
    {
      title: "遊休農地の所有者等の意向",
      filterKey: "ownerStatementIntentSurveyResultsCodes",
      tilePropertyKey: "owner_statement_intent_survey_results_code",
      options: OWNER_STATEMENT_INTENT_SURVEY_RESULT_OPTIONS,
    },
    {
      title: "農振法区分",
      filterKey: "sectionOfNoushinhouCodes",
      tilePropertyKey: "section_of_noushinhou_code",
      options: SECTION_OF_NOUSHINHOU_OPTIONS,
    },
    {
      title: "都市計画法区分",
      filterKey: "sectionOfToshikeikakuhouCodes",
      tilePropertyKey: "section_of_toshikeikakuhou_code",
      options: SECTION_OF_TOSHIKEIKAKUHOU_OPTIONS,
    },
    {
      title: "権利の種類",
      filterKey: "kindOfRightCodes",
      tilePropertyKey: "kind_of_right_code",
      options: KIND_OF_RIGHT_OPTIONS,
    },
    {
      title: "農地中間管理権の状況",
      filterKey: "rightSettingContentsCodes",
      tilePropertyKey: "right_setting_contents_code",
      options: RIGHT_SETTING_CONTENTS_OPTIONS,
    },
  ];
